import { DateRangeInput, EventClickArg, EventSourceInput } from "@fullcalendar/core";
import allLocales from "@fullcalendar/core/locales-all";
import daygridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import multimonthPlugin from "@fullcalendar/multimonth";
import FullCalendar from "@fullcalendar/react";
import timegridPlugin from "@fullcalendar/timegrid";
import { Avatar, Select } from "antd/es";
import { Guid } from "guid-typescript";
import moment from "moment";
import React, { CSSProperties, PureComponent, ReactNode } from "react";
import isEqual from "react-fast-compare";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { KMainFunctions } from "../../../shared/utilty/main-functions";
import { Icon } from "../icon/icon";
import "./calendar.scss";

declare let window: Window & { [key: string]: any };
interface ICalendarProps {
  className?: string;
  style?: CSSProperties;
  onClickDay?: () => void;
  onClickEvent?: () => void;
  onPanelChange?: () => void;
  options?: object[];
  rangeStart?: string;
  rangeEnd?: string;
  datavaluefield?: string;
  datatextfield?: string;
  datastartfield?: string;
  dataendfield?: string;
  datacolorfield?: string;
  dataheightfield?: string;
  datafontcolorfield?: string;
  datafontfamilyfield?: string;
  datafontsizefield?: string;
  datafontweightfield?: string;
  dataiconfield?: string;
  dataavatarfield?: string;
  /** Max avatars shown before an Avatar.Group "+N" chip. Carried as a string through codegen. */
  avatarMaxCount?: string | number;
  defaultPanel?: string;
  displayEventTime?: boolean;
  value?: any;
  minTime?: string | moment.Moment | Date;
  maxTime?: string | moment.Moment | Date;
}

interface ICalendarEventExtendedProps {
  height?: string;
  fontColor?: string;
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string;
  icon?: string;
  avatars?: string[];
}

// Minimal structural shape of FullCalendar's eventContent arg (the fields we
// read). Declared locally so the renderer doesn't depend on the core type
// re-export; it stays assignable to FullCalendar's `eventContent` prop.
interface ICalendarEventContentEvent {
  title: string;
  backgroundColor?: string;
  extendedProps: ICalendarEventExtendedProps;
}

interface ICalendarEventContentArg {
  event: ICalendarEventContentEvent;
  timeText: string;
}

interface ICalendarState {
  currentDate?: string;
  currentEvent?: object;
  currentPanel?: string;
  title?: string;
}

interface ICalendarViewOption {
  value: string;
  label: string;
}

interface ICalendarViewInfo {
  title: string;
}

interface ICalendarDatesSetArg {
  view: ICalendarViewInfo;
}

class Calendar extends PureComponent<ICalendarProps, ICalendarState> {
  public calendarRef: React.RefObject<FullCalendar> = React.createRef();

  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`kcalendar_${this.uniqueKey}`);

  private panel: string = "month";

  private static readonly dashboardDataIdField?: string = "id";

  private static readonly dashboardDataTitleField?: string = "title";

  private static readonly dashboardDataStartField?: string = "start";

  private static readonly dashboardDataEndField?: string = "end";

  private static readonly dashboardDataColorField?: string = "color";

  private static readonly dashboardDataHeightField?: string = "height";

  private static readonly dashboardDataFontColorField?: string = "fontColor";

  private static readonly dashboardDataFontFamilyField?: string = "fontFamily";

  private static readonly dashboardDataFontSizeField?: string = "fontSize";

  private static readonly dashboardDataFontWeightField?: string = "fontWeight";

  private static readonly dashboardDataIconField?: string = "icon";

  private static readonly dashboardDataAvatarField?: string = "avatars";

  private static readonly dashboardData = [
    {
      id: Guid.create().toString(),
      title: "Event 1",
      start: moment().subtract(2, "days").add(2, "hours").format("YYYY-MM-DD HH:mm:ss"),
      end: moment().add(2, "day").format("YYYY-MM-DD HH:mm:ss"),
      color: "#FFC300",
      height: "40px",
      fontColor: "#333333",
      fontFamily: "Arial, sans-serif",
      fontSize: "14px",
      fontWeight: "600",
      icon: "fiber_new",
      avatars: ["https://i.pravatar.cc/40?img=1", "https://i.pravatar.cc/40?img=2"]
    },
    {
      id: Guid.create().toString(),
      title: "Event 2",
      start: moment().subtract(1, "days").add(7, "hours").format("YYYY-MM-DD HH:mm:ss"),
      end: moment().add(5, "day").format("YYYY-MM-DD HH:mm:ss"),
      color: "#4AA83A",
      height: "35px",
      fontColor: "#ffffff",
      fontFamily: "Helvetica, sans-serif",
      fontSize: "12px",
      fontWeight: "400",
      icon: "edit",
      avatars: ["https://i.pravatar.cc/40?img=3", "https://i.pravatar.cc/40?img=4", "https://i.pravatar.cc/40?img=5"]
    }
  ];

  private readonly isDashboard: boolean;

  private dataIdField?: string;

  private dataTitleField?: string;

  private dataStartField?: string;

  private dataEndField?: string;

  private dataColorField?: string;

  private dataHeightField?: string;

  private dataFontColorField?: string;

  private dataFontFamilyField?: string;

  private dataFontSizeField?: string;

  private dataFontWeightField?: string;

  private dataIconField?: string;

  private dataAvatarField?: string;

  constructor(props: ICalendarProps) {
    super(props);

    this.isDashboard = window.kuika?.dashboardState === 1;

    this.initializePanelType();

    this.initializeFieldKeys();

    this.state = {
      currentDate: props.value?.currentDay ?? moment().format("YYYY-MM-DD"),
      currentEvent: props.value?.currentEvent ?? {},
      currentPanel: this.panel
    };
  }

  private get calendarApi() {
    return this.calendarRef.current?.getApi();
  }

  private initializeFieldKeys = (): void => {
    if (this.isDashboard) {
      this.dataIdField = Calendar.dashboardDataIdField;
      this.dataTitleField = Calendar.dashboardDataTitleField;
      this.dataStartField = Calendar.dashboardDataStartField;
      this.dataEndField = Calendar.dashboardDataEndField;
      this.dataColorField = Calendar.dashboardDataColorField;
      this.dataHeightField = Calendar.dashboardDataHeightField;
      this.dataFontColorField = Calendar.dashboardDataFontColorField;
      this.dataFontFamilyField = Calendar.dashboardDataFontFamilyField;
      this.dataFontSizeField = Calendar.dashboardDataFontSizeField;
      this.dataFontWeightField = Calendar.dashboardDataFontWeightField;
      this.dataIconField = Calendar.dashboardDataIconField;
      this.dataAvatarField = Calendar.dashboardDataAvatarField;
    } else {
      this.dataIdField = this.props.datavaluefield?.toLowerCase();
      this.dataTitleField = this.props.datatextfield?.toLowerCase();
      this.dataStartField = this.props.datastartfield?.toLowerCase();
      this.dataEndField = this.props.dataendfield?.toLowerCase();
      this.dataColorField = this.props.datacolorfield?.toLowerCase();
      this.dataHeightField = this.props.dataheightfield?.toLowerCase();
      this.dataFontColorField = this.props.datafontcolorfield?.toLowerCase();
      this.dataFontFamilyField = this.props.datafontfamilyfield?.toLowerCase();
      this.dataFontSizeField = this.props.datafontsizefield?.toLowerCase();
      this.dataFontWeightField = this.props.datafontweightfield?.toLowerCase();
      this.dataIconField = this.props.dataiconfield?.toLowerCase();
      this.dataAvatarField = this.props.dataavatarfield?.toLowerCase();
    }
  };

  private initializePanelType = (): void => {
    const { defaultPanel } = this.props;
    if (
      defaultPanel !== "month" &&
      defaultPanel !== "week" &&
      defaultPanel !== "day" &&
      defaultPanel !== "year" &&
      defaultPanel !== "timeGridWeek" &&
      defaultPanel !== "timeGridDay" &&
      defaultPanel !== "multiMonthYear"
    ) {
      this.panel = "month";
    } else {
      this.panel = defaultPanel;
    }
  };

  componentDidMount() {
    this.setDynamicStyle();
  }

  componentWillUnmount() {
    this.style.removeStyle();
  }

  componentDidUpdate(prevProps: ICalendarProps) {
    if (!isEqual(prevProps, this.props)) {
      this.setDynamicStyle();
    }

    if (prevProps.defaultPanel !== this.props.defaultPanel && this.props.defaultPanel) {
      this.setPanel(this.props.defaultPanel);
    }
  }

  private getClassName = (): string => {
    let result = "";

    if (this.props.className) {
      result = `${this.props.className} `;
    }

    if (!this.uniqueKey) {
      return result;
    }

    result = `${result}calendar_${this.uniqueKey.toString()}`;
    return result;
  };

  private ensurePixelUnit = (value: string | number): string => {
    if (!value) return "";

    const stringValue = String(value);

    if (/^\d+$/.test(stringValue)) {
      return `${stringValue}px`;
    }

    return stringValue;
  };

  getDynamicCss = (): string => {
    const textDecoration = this.props.style?.textDecoration;
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }
    let result = `.${className.trim()} .fc-event-time {
      text-decoration: ${textDecoration || "none"};
      flex: none;
    }`;

    result += `.${className.trim()} .fc-h-event .fc-event-title,
    .${className.trim()} .fc-daygrid-dot-event .fc-event-title {
      text-decoration: ${textDecoration || "none"};
      white-space: normal;
      padding: 0 1px !important;
    }`;

    const events: object[] = this.isDashboard ? Calendar.dashboardData : (this.props.options ?? []);
    events.forEach((item: object) => {
      const obj: { [key: string]: any } = Calendar.lowerCaseObjectKeys(item) as { [key: string]: any };
      const eventId = this.dataIdField ? obj[this.dataIdField.toLowerCase()] : undefined;
      const height = this.dataHeightField ? obj[this.dataHeightField.toLowerCase()] : undefined;
      const fontColor = this.dataFontColorField ? obj[this.dataFontColorField.toLowerCase()] : undefined;
      const fontFamily = this.dataFontFamilyField ? obj[this.dataFontFamilyField.toLowerCase()] : undefined;
      const fontSize = this.dataFontSizeField ? obj[this.dataFontSizeField.toLowerCase()] : undefined;
      const fontWeight = this.dataFontWeightField ? obj[this.dataFontWeightField.toLowerCase()] : undefined;

      if (eventId) {
        result += `.${className.trim()} .fc-event[data-event-id="${eventId}"] {`;

        if (height) {
          const heightWithPx = this.ensurePixelUnit(height);
          result += `height: ${heightWithPx} !important; min-height: ${heightWithPx} !important;`;
        }

        if (fontColor) {
          result += `color: ${fontColor} !important;`;
        }

        if (fontFamily) {
          result += `font-family: ${fontFamily} !important;`;
        }

        if (fontSize) {
          const fontSizeWithPx = this.ensurePixelUnit(fontSize);
          result += `font-size: ${fontSizeWithPx} !important;`;
        }

        if (fontWeight) {
          result += `font-weight: ${fontWeight} !important;`;
        }

        result += `}`;

        if (fontColor || fontFamily || fontSize || fontWeight) {
          result += `.${className.trim()} .fc-event[data-event-id="${eventId}"] .fc-event-title,
          .${className.trim()} .fc-event[data-event-id="${eventId}"] .fc-event-time {`;

          if (fontColor) {
            result += `color: ${fontColor} !important;`;
          }

          if (fontFamily) {
            result += `font-family: ${fontFamily} !important;`;
          }

          if (fontSize) {
            const fontSizeWithPx = this.ensurePixelUnit(fontSize);
            result += `font-size: ${fontSizeWithPx} !important;`;
          }

          if (fontWeight) {
            result += `font-weight: ${fontWeight} !important;`;
          }

          result += `}`;
        }
      }
    });

    return result;
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  private getStyle = (): CSSProperties => {
    const style = { ...this.props.style };

    if (style.color) delete style.color;
    if (style.fontSize) delete style.fontSize;
    if (style.lineHeight) delete style.lineHeight;

    return style;
  };

  private onClickDay = (dateClickArg: DateClickArg) => {
    this.setState({ currentDate: dateClickArg.dateStr }, () => {
      this.calendarRef.current?.getApi?.()?.select?.(dateClickArg.dateStr);
      this.props.onClickDay?.();
    });
  };

  private onClickEvent = (eventClickArg: EventClickArg) => {
    this.setState({ currentEvent: eventClickArg.event }, () => {
      this.props.onClickEvent?.();
    });
  };

  getCurrentDate = () => {
    return this.state.currentDate;
  };

  getPanel = () => {
    return this.panel;
  };

  setPanel = (panel?: string) => {
    if (this.panel === panel) return;

    this.panel = panel || "month";
    this.calendarApi?.changeView(this.getView(this.panel));
    this.setState({ currentPanel: this.panel });
    this.onPanelChange();
  };

  private onPrev = () => {
    this.calendarApi?.prev();
    this.onPanelChange();
  };

  private onNext = () => {
    this.calendarApi?.next();
    this.onPanelChange();
  };

  getCurrentDay = () => {
    const currentDate = this.getCurrentDate();

    if (currentDate) {
      return moment(currentDate).date();
    }
    return moment().date();
  };

  getCurrentMonth = () => {
    const currentDate = this.getCurrentDate();

    if (currentDate) {
      return moment(currentDate).month() + 1;
    }
  };

  getCurrentYear = () => {
    const currentDate = this.getCurrentDate();

    if (currentDate) {
      return moment(currentDate).year();
    }
  };

  getCurrentEvent = () => {
    return this.state.currentEvent;
  };

  getCurrentEventStartTime = () => {
    const currentEvent = this.state.currentEvent as any;
    return currentEvent?.extendedProps?.startTime;
  };

  getCurrentEventEndTime = () => {
    const currentEvent = this.state.currentEvent as any;
    return currentEvent?.extendedProps?.endTime;
  };

  getCurrentEventHeight = () => {
    const currentEvent = this.state.currentEvent as any;
    return currentEvent?.extendedProps?.height;
  };

  getCurrentEventFontColor = () => {
    const currentEvent = this.state.currentEvent as any;
    return currentEvent?.extendedProps?.fontColor;
  };

  getCurrentEventFontFamily = () => {
    const currentEvent = this.state.currentEvent as any;
    return currentEvent?.extendedProps?.fontFamily;
  };

  getCurrentEventFontSize = () => {
    const currentEvent = this.state.currentEvent as any;
    return currentEvent?.extendedProps?.fontSize;
  };

  getCurrentEventFontWeight = () => {
    const currentEvent = this.state.currentEvent as any;
    return currentEvent?.extendedProps?.fontWeight;
  };

  getCurrentRangeStart = () => {
    if (this.calendarApi) return this.calendarApi.view.currentStart;

    return moment().startOf(this.panel as any);
  };

  getCurrentRangeEnd = () => {
    if (this.calendarApi) return this.calendarApi.view.currentEnd;

    return moment().endOf(this.panel as any);
  };

  private static lowerCaseObjectKeys(obj: object): object {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v]));
  }

  private getEvents = (): EventSourceInput => {
    const events: object[] = this.isDashboard ? Calendar.dashboardData : (this.props.options ?? []);

    return events.map((item: object) => {
      const obj: { [key: string]: any } = Calendar.lowerCaseObjectKeys(item) as { [key: string]: any };
      return {
        id: this.dataIdField ? obj[this.dataIdField.toLowerCase()] : undefined,
        title: this.dataTitleField ? obj[this.dataTitleField.toLowerCase()] : undefined,
        start: this.dataStartField ? obj[this.dataStartField.toLowerCase()] : undefined,
        end: this.dataEndField ? obj[this.dataEndField.toLowerCase()] : undefined,
        color: this.dataColorField ? (obj[this.dataColorField.toLowerCase()] ?? "#1677ff") : "#1677ff",
        extendedProps: {
          height: this.dataHeightField ? obj[this.dataHeightField.toLowerCase()] : undefined,
          fontColor: this.dataFontColorField ? obj[this.dataFontColorField.toLowerCase()] : undefined,
          fontFamily: this.dataFontFamilyField ? obj[this.dataFontFamilyField.toLowerCase()] : undefined,
          fontSize: this.dataFontSizeField ? obj[this.dataFontSizeField.toLowerCase()] : undefined,
          fontWeight: this.dataFontWeightField ? obj[this.dataFontWeightField.toLowerCase()] : undefined,
          icon: this.dataIconField ? obj[this.dataIconField.toLowerCase()] : undefined,
          avatars: this.dataAvatarField ? this.normalizeAvatarUrls(obj[this.dataAvatarField.toLowerCase()]) : undefined
        } as ICalendarEventExtendedProps
      };
    });
  };

  /**
   * Normalizes a per-event avatar value into a clean URL list. Tolerates a
   * real array, a JSON-array string (`["u1","u2"]`), a single value, or a
   * comma-separated list — while never splitting a single `data:` URI (which
   * legitimately contains a comma).
   */
  private normalizeAvatarUrls = (value: unknown): string[] => {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.map((v: unknown): string => String(v).trim()).filter(Boolean);
    }

    if (typeof value !== "string") {
      return [String(value).trim()].filter(Boolean);
    }

    const trimmed: string = value.trim();
    if (!trimmed) {
      return [];
    }

    if (trimmed.startsWith("[")) {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((v: unknown): string => String(v).trim()).filter(Boolean);
        }
      } catch {
        // not valid JSON — fall through to the single-value / CSV handling
      }
    }

    if (trimmed.startsWith("data:")) {
      return [trimmed];
    }

    if (trimmed.includes(",")) {
      return trimmed
        .split(",")
        .map((v: string): string => v.trim())
        .filter(Boolean);
    }

    return [trimmed];
  };

  private getAvatarMaxCount = (): number | undefined => {
    const raw: string | number | undefined = this.props.avatarMaxCount;
    if (raw === undefined || raw === null || raw === "") {
      return undefined;
    }
    const parsed: number = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  };

  /**
   * Custom event renderer: an optional leading icon next to the title (row 1)
   * and an avatar group pinned to the bottom-right (last row). Returns `true`
   * for events without an icon or avatars so FullCalendar keeps its DEFAULT
   * rendering — in FullCalendar v6 returning `undefined`/nothing renders an
   * EMPTY container instead, so `true` is required (zero regression).
   */
  private renderEventContent = (arg: ICalendarEventContentArg): ReactNode | boolean => {
    const extended: ICalendarEventExtendedProps = arg.event.extendedProps;
    const iconName: string | undefined = extended.icon;
    const avatars: string[] = Array.isArray(extended.avatars) ? extended.avatars : [];
    const hasIcon: boolean = Boolean(iconName);
    const hasAvatars: boolean = avatars.length > 0;

    if (!hasIcon && !hasAvatars) {
      return true;
    }

    const avatarSize: number = 20;

    return (
      <div className="kuika-calendar-event">
        <div className="kuika-calendar-event__title-row">
          {arg.event.backgroundColor ? (
            <span className="fc-daygrid-event-dot" style={{ borderColor: arg.event.backgroundColor }} />
          ) : null}
          {hasIcon ? (
            <Icon iconName={iconName as string} style={{ fontSize: "14px", marginRight: 4, lineHeight: 1 }} />
          ) : null}
          {arg.timeText ? <span className="fc-event-time">{arg.timeText}</span> : null}
          <span className="fc-event-title">{arg.event.title}</span>
        </div>
        {hasAvatars ? (
          <div className="kuika-calendar-event__avatars">
            <Avatar.Group maxCount={this.getAvatarMaxCount()} size={avatarSize}>
              {avatars.map((url: string, index: number) => (
                <Avatar
                  key={`${url}-${index}`}
                  size={avatarSize}
                  src={KMainFunctions.resolveImageSrc(url, {
                    width: avatarSize,
                    height: avatarSize,
                    imageFit: 1,
                    cacheBust: false
                  })}
                  icon={<Icon iconName="person" style={{ fontSize: "14px", lineHeight: 1 }} />}
                />
              ))}
            </Avatar.Group>
          </div>
        ) : null}
      </div>
    );
  };

  private getLocale = () => {
    let lang = localStorage.getItem("ml")?.split("_")[0];

    return lang || "en";
  };

  private onPanelChange = () => {
    this.props.onPanelChange?.();
  };

  private getValidRange = (): DateRangeInput => {
    return {
      start: this.props.rangeStart ? moment(this.props.rangeStart).format("YYYY-MM-DD") : undefined,
      end: this.props.rangeEnd ? moment(this.props.rangeEnd).format("YYYY-MM-DD") : undefined
    };
  };

  private getView = (panel?: string): string => {
    if (panel === "month") {
      return "dayGridMonth";
    }

    if (panel === "week") {
      return "timeGridWeek";
    }

    if (panel === "day") {
      return "timeGridDay";
    }

    if (panel === "year") {
      return "multiMonthYear";
    }

    return "dayGridMonth";
  };

  private getTimeSlot = (value?: string | moment.Moment | Date): string | undefined => {
    if (!value) return;

    const slotTime = moment(value);

    if (!slotTime) return;

    return slotTime.format("HH:mm:ss");
  };

  render(): ReactNode {
    const calendarLocale = this.getLocale();
    const buttonTexts = allLocales.find((locale) => locale.code === calendarLocale)?.buttonText;
    const viewOptions: ICalendarViewOption[] = [
      { value: "day", label: buttonTexts?.day || "day" },
      { value: "week", label: buttonTexts?.week || "week" },
      { value: "month", label: buttonTexts?.month || "month" },
      { value: "year", label: buttonTexts?.year || "year" }
    ];

    return (
      // position+z-index makes the calendar its own stacking context, so
      // FullCalendar's high z-index "+more" popover stays contained below
      // page-level overlays (e.g. an open Drawer) instead of painting over them.
      <div className={this.getClassName()} style={{ position: "relative", zIndex: 0, ...this.getStyle() }}>
        <div className="fc fc-theme-standard fc-direction-ltr kuika-calendar-toolbar">
          <div className="fc-toolbar fc-header-toolbar">
            <div className="fc-toolbar-chunk">
              <div className="fc-button-group">
                <button type="button" className="fc-button fc-button-primary" aria-label="prev" onClick={this.onPrev}>
                  <span className="fc-icon fc-icon-chevron-left" />
                </button>
                <button type="button" className="fc-button fc-button-primary" aria-label="next" onClick={this.onNext}>
                  <span className="fc-icon fc-icon-chevron-right" />
                </button>
              </div>
            </div>
            <div className="fc-toolbar-chunk">
              <h2 className="fc-toolbar-title">{this.state.title}</h2>
            </div>
            <div className="fc-toolbar-chunk kuika-calendar-view-dropdown">
              <Select
                value={this.state.currentPanel}
                onChange={(value: string) => this.setPanel(value)}
                options={viewOptions}
                style={{ minWidth: 110 }}
              />
            </div>
          </div>
        </div>
        <FullCalendar
          ref={this.calendarRef}
          plugins={[daygridPlugin, timegridPlugin, multimonthPlugin, interactionPlugin]}
          firstDay={1}
          validRange={this.getValidRange()}
          initialView={this.getView(this.props.defaultPanel)}
          displayEventTime={this.props.displayEventTime}
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
          }}
          locale={calendarLocale}
          locales={allLocales}
          events={this.getEvents()}
          eventContent={this.dataIconField || this.dataAvatarField ? this.renderEventContent : undefined}
          eventDidMount={(info) => {
            if (info.event.id) {
              info.el.setAttribute("data-event-id", info.event.id);
            }
          }}
          datesSet={(arg: ICalendarDatesSetArg) => {
            if (arg.view.title !== this.state.title) {
              this.setState({ title: arg.view.title });
            }
          }}
          headerToolbar={false}
          slotMinTime={this.getTimeSlot(this.props.minTime) || "00:00:00"}
          slotMaxTime={this.getTimeSlot(this.props.maxTime) || "24:00:00"}
          dateClick={this.onClickDay}
          eventClick={this.onClickEvent}
          height={this.props.style?.height || "auto"}
        />
      </div>
    );
  }
}

const calendar = withCommonEvents(Calendar);
export { calendar as Calendar };
